import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Card, Table, Tag, Button, Form, Modal, Input, Switch, Alert, Select, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { PlusOutlined } from '@ant-design/icons';
import { useMyContext } from 'Context/MyContextProvider';

// IP addresses are stored as a comma-separated string (e.g. "1.1.1.1,2.2.2.2").
const parseIpList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.map((ip) => String(ip).trim()).filter(Boolean);
    }
    return String(value)
        .split(',')
        .map((ip) => ip.trim())
        .filter(Boolean);
};

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};
const EditableCell = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    showAlert,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
        }
    }, [editing]);
    const toggleEdit = () => {
        setEditing(!editing);
        const fieldValue = dataIndex === 'ip_addresses'
            ? parseIpList(record[dataIndex])
            : record[dataIndex];
        form.setFieldsValue({
            [dataIndex]: fieldValue,
        });
    };
    const save = async () => {
        try {
            const values = await form.validateFields();

            toggleEdit();

            const updatedValues = { ...values };
            if (dataIndex === 'ip_addresses') {
                // Deduplicate and re-join the tags back into a comma-separated string.
                const ips = [...new Set(parseIpList(updatedValues.ip_addresses))];
                updatedValues.ip_addresses = ips.length ? ips.join(',') : null;
            } else if (updatedValues[dataIndex] === '' || updatedValues[dataIndex] === undefined) {
                updatedValues[dataIndex] = null;
            }

            handleSave({
                ...record,
                ...updatedValues,
            });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
            if (errInfo?.errorFields) {
                const errorMsg = errInfo.errorFields[0]?.errors[0] || 'Validation failed';
                if (showAlert) {
                    showAlert(errorMsg, 'error');
                }
            }
        }
    };
    let childNode = children;
    if (editable) {
        const value = record?.[dataIndex];
        const isEmpty = value === undefined || value === null || value === '';
        const isRequired = dataIndex === 'ip_addresses' && (record?.ip_auth === true || record?.ip_auth === 1 || record?.ip_auth === "1");

        childNode = editing ? (
            <Form.Item
                style={{
                    margin: 0,
                }}
                name={dataIndex}
                rules={[
                    {
                        required: isRequired,
                        message: `${title} is required.`,
                    },
                ]}
            >
                <Select
                    ref={inputRef}
                    mode="tags"
                    open={false}
                    tokenSeparators={[',']}
                    style={{ width: '100%' }}
                    placeholder="Type an IP and press comma to add"
                    onBlur={save}
                />
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{
                    paddingRight: 24,
                    minHeight: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px',
                    cursor: 'pointer',
                }}
                onClick={toggleEdit}
            >
                {isEmpty ? (
                    <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>
                        Click to enter IP
                    </span>
                ) : (
                    parseIpList(value).map((ip) => (
                        <Tag key={ip} color="blue" className="mr-0">
                            {ip}
                        </Tag>
                    ))
                )}
            </div>
        );
    }
    return <td {...restProps}>{childNode}</td>;
};

const ApiKey = () => {

    const { api, authToken, UserData, emailAlert, HandleSendEmail } = useMyContext();

    const [loading, setLoading] = useState(false);

    const [apiKeys, setApiKeys] = useState([]);

    const [newKey, setNewKey] = useState('');

    const [alertConfig, setAlertConfig] = useState(null); // { message: string, type: 'error' | 'warning' | 'success' }

    const showAlert = (message, type = 'error') => {
        setAlertConfig({ message, type });
    };

    useEffect(() => {
        if (alertConfig) {
            const timer = setTimeout(() => {
                setAlertConfig(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [alertConfig]);

    //role
    const ApiKeyData = useCallback(async () => {
        try {
            const response = await apiClient.get(`${api}api-key/${UserData?.id}`,
                {
                    headers: {
                        'Authorization': 'Bearer ' + authToken,
                    }
                });
            const data = response?.data?.api_key;
            setApiKeys(data);

            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
    }, [api, UserData?.id, authToken]);

    useEffect(() => {
        setLoading(true);
        ApiKeyData();
    }, [ApiKeyData])


    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const handleSave = useCallback(async (row) => {
        // console.log(row);return
        try {
            await apiClient.post(`${api}update-key/${row?.id}`,
                {
                    row
                }, {
                headers: {
                    Authorization: "Bearer " + authToken,
                },
            });
            ApiKeyData();
        } catch (error) {
            console.log(error);
            const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to save';
            showAlert(errMsg, 'error');
        }
    }, [api, authToken, ApiKeyData]);

    const UpdateSetting = useCallback(async (id, value) => {
        try {
            await apiClient.post(`${api}update-key/${id}`,
                {
                    checked: value,
                    ip_addresses: !value ? null : undefined,
                    row: !value ? { ip_addresses: null } : undefined
                }, {
                headers: {
                    Authorization: "Bearer " + authToken,
                },
            });
            ApiKeyData();
        } catch (error) {
            console.log(error);
            const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to update setting';
            showAlert(errMsg, 'error');
        }
    }, [api, authToken, ApiKeyData]);

    const handleSwitchChange = useCallback(async (id, checked) => {
        if (id) {
            UpdateSetting(id, checked)
        }
    }, [UpdateSetting]);

    const columns = useMemo(() => {
        const defaultColumns = [
            {
                title: '#',
                dataIndex: 'id',
                sorter: {
                    compare: (a, b) => a.id.length - b.id.length,
                },
                render: (text, record, index) => `${index + 1}`,
            },
            {
                title: 'Key',
                dataIndex: 'key',
                sorter: {
                    compare: (a, b) => a.key.length - b.key.length,
                },
            },
            {
                title: 'Generated At',
                dataIndex: 'created_at',
                render: (created_at) => {
                    const date = new Date(created_at);
                    const day = ("0" + date.getDate()).slice(-2);
                    const month = ("0" + (date.getMonth() + 1)).slice(-2);
                    const year = date.getFullYear();
                    let hours = date.getHours();
                    const minutes = ("0" + date.getMinutes()).slice(-2);
                    const seconds = ("0" + date.getSeconds()).slice(-2);
                    let ampm = hours >= 12 ? 'PM' : 'AM';
                    hours = hours % 12;
                    hours = hours ? hours : 12; // Handle midnight
                    const formattedTime = `${hours}:${minutes}:${seconds} ${ampm}`;
                    return `${day}/${month}/${year} | ${formattedTime}`;
                },
                sorter: {
                    compare: (a, b) => a.created_at.length - b.created_at.length,
                },
            },
            {
                title: 'IP Validation',
                dataIndex: 'chatbot_access',
                render: (text, record, index) => {
                    let checked = record?.ip_auth === true || record?.ip_auth === 1 || record?.ip_auth === "1";
                    return <Switch checked={checked} onChange={checked => handleSwitchChange(record.id, checked)} />;
                },
                // sorter: (a, b) => utils.antdTableSorter(a, b, 'number')
            },
            {
                title: 'IP Address',
                dataIndex: 'ip_addresses',
                width: '30%',
                editable: true,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                render: (_, record) => {
                    const isActive = record.status === true || record.status === 'true' || record.status === 1 || record.status === '1';
                    return (
                        <div className="">
                            <Tag className="mr-0" color={isActive ? 'cyan' : 'red'}>
                                {isActive ? 'Active' : 'Inactive'}
                            </Tag>
                        </div>
                    );
                },
                sorter: {
                    compare: (a, b) => {
                        const statusA = String(a?.status || '');
                        const statusB = String(b?.status || '');
                        return statusA.localeCompare(statusB);
                    },
                },
            },
        ];

        return defaultColumns.map((col) => {
            if (!col.editable) {
                return col;
            }
            return {
                ...col,
                onCell: (record) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave,
                    showAlert: (msg, type) => showAlert(msg, type),
                }),
            };
        });
    }, [handleSave, handleSwitchChange]);

    //model
    const [open, setOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [isKeyGenerated, setIsKeyGenerated] = useState(false);

    const showModal = () => {
        setOpen(true);
    };

    const handleOk = async () => {
        // setModalText('The modal will be closed after two seconds');
        setConfirmLoading(true);
        try {
             await apiClient.post(`${api}store-key`,
                {
                    key: newKey,
                    user_id: UserData?.id
                }, {
                headers: {
                    Authorization: "Bearer " + authToken,
                },
            });
            ApiKeyData();
            setConfirmLoading(false);
            if (emailAlert) {
                HandleSendEmail(
                    UserData.id,
                    { template: 'API Key Generate', email: UserData?.email }
                )
            }
            handleCancel()
        } catch (error) {
            setConfirmLoading(false);
            handleCancel()
            console.log(error);
        }

    };
    const handleCancel = () => {
        setNewKey('')
        setIsKeyGenerated(false);
        setOpen(false);
    };

    const generateRandomString = (length) => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    };

    const HandleKey = () => {
        const key = generateRandomString(20)
        setNewKey(key)
        setIsKeyGenerated(true)
    }

    return (
        <>
            <Modal
                title="Generate New Key"
                open={open}
                onOk={handleOk}
                confirmLoading={confirmLoading}
                okButtonProps={{ disabled: !isKeyGenerated }}
                onCancel={handleCancel}
                destroyOnClose
            >
                <Form.Item label="" className=" my-3">
                    <Input type="text"
                        value={newKey}
                        // onChange={(e) => setName(e.target.value)}
                        placeholder="Your Key"
                        disabled
                    />
                </Form.Item>
                <div className='text-center'>
                    <Button onClick={HandleKey}><SettingOutlined />Generate New Key</Button>
                </div>
            </Modal>
            <Card
                title="Manage Api Keys"
                extra={
                    <Button type="primary" className="ml-2" onClick={showModal}>
                        <PlusOutlined />
                        <span>Generate New</span>
                    </Button>
                }>
                {alertConfig && (
                    <Alert
                        message={alertConfig.message}
                        type={alertConfig.type}
                        showIcon
                        closable
                        onClose={() => setAlertConfig(null)}
                        className="mb-4"
                    />
                )}
                <div className="table-responsive">
                    <Table
                        components={components}
                        rowClassName={() => 'editable-row'}
                        loading={loading}
                        columns={columns}
                        dataSource={apiKeys}
                        rowKey="id"
                        footer={() => (
                            <Typography.Text type="secondary">
                                Note: You can add multiple IP addresses. Click the IP Address cell,
                                type an IP and press comma (<Typography.Text code>,</Typography.Text>) to add each one as a separate tag.
                            </Typography.Text>
                        )} />
                </div>
                {/* <UserView data={selectedUser} visible={userProfileVisible} close={() => closeUserProfile()} /> */}
            </Card >
        </>
    );
};

export default ApiKey
